/**
 * Theme Colors - Single source of truth for design tokens
 * Mirrors tailwind.config.js colors for programmatic use (charts, canvas, etc.)
 *
 * WCAG AA Compliance Notes:
 * - For white text: use primary-700+ (5.24:1 contrast)
 * - primary-500 (2.77:1) FAILS AA for normal text
 *
 * Created: 2026-02-05 (GAP-UX-001, GAP-UX-002)
 */

// Primary - Sky Blue
export const primary = {
  50: '#f0f9ff',
  100: '#e0f2fe',
  200: '#bae6fd',
  300: '#7dd3fc',
  400: '#38bdf8',
  500: '#0ea5e9',
  600: '#0284c7',
  700: '#0369a1',
  800: '#075985',
  900: '#0c4a6e',
} as const;

// Surface - Neutral dark backgrounds
export const surface = {
  900: '#0f0f12',
  800: '#1a1a24',
  700: '#242432',
  600: '#2a2a3a',
  500: '#3a3a4a',
  400: '#4a4a5a',
} as const;

// Semantic colors (softened - TASK-093)
export const success = {
  300: '#86efac',
  400: '#6ee7b7',
  500: '#34d399',
  600: '#10b981',
} as const;

export const warning = {
  300: '#fcd34d',
  400: '#fbbf24',
  500: '#f59e0b',
  600: '#d97706',
} as const;

export const danger = {
  300: '#fca5a5',
  400: '#f9a8a8',
  500: '#f87171',
  600: '#ef4444',
} as const;

export const info = {
  400: '#a5b4fc',
  500: '#818cf8',
  600: '#6366f1',
} as const;

// Accent colors for charts and data visualization
export const accent = {
  blue: '#38bdf8',
  purple: '#a5b4fc',
  amber: '#fbbf24',
  coral: '#fb7185',
  emerald: '#6ee7b7',
} as const;

// Text colors
export const text = {
  primary: '#f1f5f9',
  secondary: '#cbd5e1',
  tertiary: '#94a3b8',
} as const;

/**
 * Chart color palette - WCAG AA compliant, distinguishable colors
 * Use for multi-series charts, legends, pie charts, etc.
 */
export const CHART_COLORS = [
  primary[400],   // Sky Blue - primary series
  warning[400],   // Amber - secondary series
  accent.coral,   // Coral - tertiary series
  info[400],      // Lavender - quaternary series
  success[400],   // Emerald - positive/growth
  danger[500],    // Red - danger/decline
  accent.purple,  // Purple - additional series
  primary[300],   // Light blue - fallback
] as const;

/**
 * Waterfall chart colors - for gain/loss visualization
 */
export const WATERFALL_COLORS = {
  positive: success[400],  // #6ee7b7 - Emerald for growth
  negative: danger[500],   // #f87171 - Red for decline
  neutral: surface[500],   // #3a3a4a - Gray for baseline/total
  result: primary[400],    // #38bdf8 - Sky blue for net result
} as const;

/**
 * Comparison colors - for before/after, A/B comparisons
 */
export const COMPARISON_COLORS = {
  before: danger[400],     // Coral-ish red for "before"
  after: success[400],     // Green for "after"
  baseline: surface[400],  // Gray for baseline
} as const;

// Export default theme object for convenience
const theme = {
  primary,
  surface,
  success,
  warning,
  danger,
  info,
  accent,
  text,
  charts: {
    default: CHART_COLORS,
    waterfall: WATERFALL_COLORS,
    comparison: COMPARISON_COLORS,
  },
} as const;

export default theme;
